<template>
  <!-- The body of one section (the heading is the layout's). `layout` is how entries are set out:
       default (dates on the right), tabular (dates in a column on the left), or stacked (for a
       narrow column). `timeline` draws a line and a dot beside each entry. -->
  <div>
    <p v-if="section.key === 'summary'" class="whitespace-pre-line">{{ content.summary }}</p>

    <div v-else-if="section.key === 'skills'" :class="layout === 'stacked' ? 'space-y-1.5' : 'space-y-0.5'">
      <div v-for="group in content.skills" :key="group.id" :class="layout === 'tabular' ? 'grid grid-cols-[26%_1fr] gap-4' : ''">
        <p v-if="layout === 'stacked'" class="font-semibold">{{ group.name }}</p>
        <p v-else-if="layout === 'tabular'" class="font-semibold">{{ group.name }}</p>
        <p v-if="layout === 'stacked'" class="opacity-90">{{ group.items.join(' · ') }}</p>
        <p v-else-if="layout === 'tabular'">{{ group.items.join(', ') }}</p>
        <p v-else><span v-if="group.name" class="font-semibold">{{ group.name }}: </span>{{ group.items.join(', ') }}</p>
      </div>
    </div>

    <div v-else-if="section.key === 'languages'" :class="layout === 'tabular' ? 'space-y-0.5' : layout === 'stacked' ? 'space-y-0.5' : ''">
      <template v-if="layout === 'default'">
        <p>
          <template v-for="(language, index) in content.languages" :key="language.id">
            <span class="font-semibold">{{ language.name }}</span><span v-if="language.level"> ({{ language.level }})</span><span v-if="index < content.languages.length - 1">, </span>
          </template>
        </p>
      </template>
      <template v-else>
        <div v-for="language in content.languages" :key="language.id" :class="layout === 'tabular' ? 'grid grid-cols-[26%_1fr] gap-4' : 'flex justify-between gap-2'">
          <p class="font-semibold">{{ language.name }}</p>
          <p :class="layout === 'stacked' ? 'opacity-90' : ''">{{ language.level }}</p>
        </div>
      </template>
    </div>

    <div v-else :class="layout === 'stacked' ? 'space-y-2' : 'space-y-2.5'">
      <div v-for="entry in section.entries" :key="entry.id" class="resume-entry" :class="timeline ? 'relative pl-4 border-l-2 ml-1' : ''" :style="timeline ? { borderColor: `${accent}55` } : null">
        <span v-if="timeline" class="absolute -left-[5px] top-[0.45em] w-2 h-2 rounded-full" :style="{ backgroundColor: accent }" aria-hidden="true"></span>

        <!-- dates in a column on the left -->
        <div v-if="layout === 'tabular'" class="grid grid-cols-[26%_1fr] gap-4">
          <div class="text-[0.92em] text-gray-600">
            <p v-if="date(entry)" class="whitespace-nowrap">{{ date(entry) }}</p>
            <p v-if="entry.location">{{ entry.location }}</p>
          </div>
          <div class="min-w-0">
            <p><span class="font-semibold">{{ title(entry) }}</span><span v-if="subtitle(entry)" class="text-gray-700"> · {{ subtitle(entry) }}</span></p>
            <p v-if="entry.grade" class="text-[0.9em] text-gray-500">{{ entry.grade }}</p>
            <p v-if="entry.description" class="mt-0.5 whitespace-pre-line">{{ entry.description }}</p>
            <ul v-if="entry.bullets?.length" class="mt-0.5 list-disc pl-5 space-y-0.5"><li v-for="(bullet, index) in entry.bullets" :key="index">{{ bullet }}</li></ul>
          </div>
        </div>

        <!-- title, then the date under it, for a narrow column -->
        <div v-else-if="layout === 'stacked'">
          <p class="font-semibold">{{ title(entry) }}</p>
          <p v-if="subtitle(entry)" class="opacity-90">{{ subtitle(entry) }}</p>
          <p v-if="date(entry)" class="text-[0.9em] opacity-75">{{ date(entry) }}</p>
        </div>

        <!-- the usual: title on the left, date on the right -->
        <template v-else>
          <div class="flex items-baseline justify-between gap-3">
            <p class="min-w-0"><span class="font-semibold">{{ title(entry) }}</span><span v-if="subtitle(entry)" class="text-gray-700"> · {{ subtitle(entry) }}</span></p>
            <p v-if="date(entry)" class="flex-shrink-0 text-[0.9em] text-gray-500 whitespace-nowrap">{{ date(entry) }}</p>
          </div>
          <p v-if="entry.location" class="text-[0.9em] text-gray-500">{{ entry.location }}</p>
          <p v-if="entry.grade" class="text-[0.9em] text-gray-500">{{ entry.grade }}</p>
          <p v-if="entry.description" class="mt-0.5 whitespace-pre-line">{{ entry.description }}</p>
          <ul v-if="entry.bullets?.length" class="mt-0.5 list-disc pl-5 space-y-0.5"><li v-for="(bullet, index) in entry.bullets" :key="index">{{ bullet }}</li></ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { entryDate, entrySubtitle, entryTitle } from './resumeView.js'

const props = defineProps({
  section: { type: Object, required: true }, // { key, title, entries } from useResumeView
  content: { type: Object, required: true },
  layout: { type: String, default: 'default' }, // default | tabular | stacked
  timeline: { type: Boolean, default: false },
  accent: { type: String, default: '#2563eb' },
})

const title = (entry) => entryTitle(props.section.key, entry)
const subtitle = (entry) => entrySubtitle(props.section.key, entry)
const date = (entry) => entryDate(props.section.key, entry)
</script>
